/**
 * pitfalls.ts — queue #666 §UAT-2026-07-09-R666 §R666-2.
 *
 * 2달여 라이브 운영에서 축적된 재발성 버그클래스를 손수 큐레이션한 정적 데이터 +
 * 순수 파생 함수. 데모/가져다쓰기/레시피/설정은 완비됐으나 이 지식은 지금까지
 * `docs/biz-specs/*.md` UAT/R-delta ★ 서술 · `docs/common-specs/**` 주의 서술 · git `fix(`
 * 이력 · 운영 메모(MEMORY)에만 흩어져 소비 개발자에게 노출되지 않았다 — 같은 함정을
 * 반복하지 않도록 증상→원인→해결 + 실존 모듈 코드 매핑으로 표면화한다.
 *
 * ★재사용·순수파생 원칙(§R664 recipes.ts 계보 답습) — 함정은 손수 발굴·큐레이션한
 * 서술형 지식이라 빌드타임 추출/git 파싱 대상이 아니다(신규 추출 스크립트 0). 사람이
 * 읽는 텍스트(title/symptom/cause/fix)는 전체 i18n 키(ko/en 대칭 · §R656 로케일 부채
 * 재발 방지). `snippet`/`ref` 만 정적 문자열(로케일 무관).
 * ★시한폭탄 없음 — 함정 데이터·파생은 일자 의존 0(정적 지식). `now` 주입 규약 해당 없음.
 * ★재구현 금지 — 함정은 재발성 지식의 정적 표현일 뿐 실행 코드 0줄. 해결책은 공통/
 * 프레임워크 표준 사용을 안내한다(자체 우회 구현 유도 없음).
 */

export type PitfallSeverity = 'high' | 'medium' | 'low';

/** 알려진 함정 1건 — 실존 모듈 코드 매핑 + 증상/원인/해결(i18n 키) + 선택 스니펫 */
export interface Pitfall {
  id: string; // kebab-case 안정 id(딥링크 键) — 예 'optional-dep-ncdfe'
  moduleCodes: string[]; // 실존 코드(take-this.json 키) — 예 ['C017','C053']
  severity: PitfallSeverity; // 심각도(정렬·필터)
  keywords: string[]; // 검색·필터 태그(로케일 무관 소문자) — 예 ['ncdfe','conditional','startup']
  titleKey: string; // showcase.pitfall.<id>.title (짧은 라벨 · 카드/검색 제목)
  symptomKey: string; // showcase.pitfall.<id>.symptom (개발자가 겪는 현상)
  causeKey: string; // showcase.pitfall.<id>.cause (근본 원인)
  fixKey: string; // showcase.pitfall.<id>.fix (구체 해결)
  snippet?: string; // (선택) 해결 코드/설정 스니펫(정적 문자열 · i18n 대상 아님)
  ref?: string; // (선택) 출처 델타/커밋/이슈 참조(예 'R617 #493' · 정적)
}

const SEVERITY_RANK: Record<PitfallSeverity, number> = { high: 0, medium: 1, low: 2 };
export const severityRank = (s: PitfallSeverity): number => SEVERITY_RANK[s];

/** 특정 모듈 코드에 매핑된 함정만 · 심각도 asc(high 먼저) 정렬. */
export function pitfallsForModule(code: string, pitfalls: Pitfall[]): Pitfall[] {
  return pitfalls
    .filter((p) => p.moduleCodes.includes(code))
    .sort((a, b) => severityRank(a.severity) - severityRank(b.severity));
}

/** 증상/원인/제목/키워드 부분일치(대소문자 무시) + (선택) 심각도/모듈 파셋 필터. */
export function filterPitfalls(
  pitfalls: Pitfall[],
  opts: { query?: string; severity?: PitfallSeverity; moduleCode?: string },
  t: (key: string) => string,
): Pitfall[] {
  const q = (opts.query ?? '').trim().toLowerCase();
  return pitfalls
    .filter((p) => (opts.severity ? p.severity === opts.severity : true))
    .filter((p) => (opts.moduleCode ? p.moduleCodes.includes(opts.moduleCode) : true))
    .filter((p) => {
      if (!q) return true;
      const hay = [t(p.titleKey), t(p.symptomKey), t(p.causeKey), ...p.keywords]
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    })
    .sort((a, b) => severityRank(a.severity) - severityRank(b.severity));
}

// ─── 알려진 함정 시드(§R666-2 표에서 손수 큐레이션 · 실존 사례) ────────────────────
// ★출처 표 자체는 25행(gRPC bind/scheme 이 서로 다른 severity·fix 를 가진 별개 행 2건으로
// 분리 표기) — 변경 요약 서술문의 "24건"은 이 둘을 한 문장으로 묶어 센 요약 수치이며,
// 표 자체(SSOT)는 25건이다. "임의 창작·누락 금지" 원칙에 따라 표의 행을 그대로 전체
// 옮긴다(1건도 임의로 드롭하지 않음).

export const PITFALLS: Pitfall[] = [
  {
    id: 'optional-dep-ncdfe',
    moduleCodes: ['C017', 'C053', 'C054', 'C055'],
    severity: 'high',
    keywords: ['ncdfe', 'conditional', 'startup', 'optional-dependency', 'classpath'],
    titleKey: 'showcase.pitfall.optional-dep-ncdfe.title',
    symptomKey: 'showcase.pitfall.optional-dep-ncdfe.symptom',
    causeKey: 'showcase.pitfall.optional-dep-ncdfe.cause',
    fixKey: 'showcase.pitfall.optional-dep-ncdfe.fix',
    ref: 'R617 #493',
  },
  {
    id: 'nested-config-rootscan',
    moduleCodes: ['C017'],
    severity: 'high',
    keywords: ['rootscan', 'nested-configuration', 'conditionalonclass', 'startup'],
    titleKey: 'showcase.pitfall.nested-config-rootscan.title',
    symptomKey: 'showcase.pitfall.nested-config-rootscan.symptom',
    causeKey: 'showcase.pitfall.nested-config-rootscan.cause',
    fixKey: 'showcase.pitfall.nested-config-rootscan.fix',
    ref: '2026-07-02',
  },
  {
    id: 'optin-broker-health-down',
    moduleCodes: ['C053', 'C054', 'C057'],
    severity: 'high',
    keywords: ['health', 'opt-in', 'broker', 'deploy-gate', 'mongo'],
    titleKey: 'showcase.pitfall.optin-broker-health-down.title',
    symptomKey: 'showcase.pitfall.optin-broker-health-down.symptom',
    causeKey: 'showcase.pitfall.optin-broker-health-down.cause',
    fixKey: 'showcase.pitfall.optin-broker-health-down.fix',
    snippet: 'management.health.mongo.enabled: false\nmanagement.health.rabbit.enabled: false\nmanagement.health.elasticsearch.enabled: false',
    ref: '2026-07-03',
  },
  {
    id: 'startup-db-query-gateway',
    moduleCodes: ['C006', 'G014'],
    severity: 'high',
    keywords: ['gateway', 'applicationreadyevent', 'startup', 'db-query'],
    titleKey: 'showcase.pitfall.startup-db-query-gateway.title',
    symptomKey: 'showcase.pitfall.startup-db-query-gateway.symptom',
    causeKey: 'showcase.pitfall.startup-db-query-gateway.cause',
    fixKey: 'showcase.pitfall.startup-db-query-gateway.fix',
    ref: '2026-06-16',
  },
  {
    id: 'json-nonnull-undefined',
    moduleCodes: ['C002'],
    severity: 'medium',
    keywords: ['jackson', 'non_null', 'undefined', 'contract-drift'],
    titleKey: 'showcase.pitfall.json-nonnull-undefined.title',
    symptomKey: 'showcase.pitfall.json-nonnull-undefined.symptom',
    causeKey: 'showcase.pitfall.json-nonnull-undefined.cause',
    fixKey: 'showcase.pitfall.json-nonnull-undefined.fix',
    snippet: 'if (dto.field != null) { /* undefined 도 함께 처리 */ }',
    ref: '#616',
  },
  {
    id: 'env-relaxed-binding',
    moduleCodes: ['C016'],
    severity: 'high',
    keywords: ['env', 'relaxed-binding', 'configurationproperties', 'canonical'],
    titleKey: 'showcase.pitfall.env-relaxed-binding.title',
    symptomKey: 'showcase.pitfall.env-relaxed-binding.symptom',
    causeKey: 'showcase.pitfall.env-relaxed-binding.cause',
    fixKey: 'showcase.pitfall.env-relaxed-binding.fix',
    snippet: 'eraf.common.slow-query.threshold-ms → ERAF_COMMON_SLOWQUERY_THRESHOLDMS',
    ref: '#615',
  },
  {
    id: 'jpa-convert-seed-jdbc',
    moduleCodes: ['C042', 'C043'],
    severity: 'medium',
    keywords: ['jpa', 'convert', 'attributeconverter', 'flyway', 'seed'],
    titleKey: 'showcase.pitfall.jpa-convert-seed-jdbc.title',
    symptomKey: 'showcase.pitfall.jpa-convert-seed-jdbc.symptom',
    causeKey: 'showcase.pitfall.jpa-convert-seed-jdbc.cause',
    fixKey: 'showcase.pitfall.jpa-convert-seed-jdbc.fix',
    ref: '#613~614',
  },
  {
    id: 'bean-name-collision',
    moduleCodes: ['C042'],
    severity: 'high',
    keywords: ['bean-name', 'collision', 'startup', 'simple-name'],
    titleKey: 'showcase.pitfall.bean-name-collision.title',
    symptomKey: 'showcase.pitfall.bean-name-collision.symptom',
    causeKey: 'showcase.pitfall.bean-name-collision.cause',
    fixKey: 'showcase.pitfall.bean-name-collision.fix',
    snippet: '@Component("bizXxxService")',
    ref: '2026-06-17',
  },
  {
    id: 'tenant-id-global-pollution',
    moduleCodes: ['C046'],
    severity: 'high',
    keywords: ['tenantid', 'multitenancy', 'filter', 'query'],
    titleKey: 'showcase.pitfall.tenant-id-global-pollution.title',
    symptomKey: 'showcase.pitfall.tenant-id-global-pollution.symptom',
    causeKey: 'showcase.pitfall.tenant-id-global-pollution.cause',
    fixKey: 'showcase.pitfall.tenant-id-global-pollution.fix',
    ref: '2026-06-17',
  },
  {
    id: 'conditional-bean-hard-inject',
    moduleCodes: ['C020'],
    severity: 'high',
    keywords: ['conditional-bean', 'autowired', 'objectprovider', 'feature-toggle'],
    titleKey: 'showcase.pitfall.conditional-bean-hard-inject.title',
    symptomKey: 'showcase.pitfall.conditional-bean-hard-inject.symptom',
    causeKey: 'showcase.pitfall.conditional-bean-hard-inject.cause',
    fixKey: 'showcase.pitfall.conditional-bean-hard-inject.fix',
    snippet: '@Autowired(required = false)\nprivate Optional<FeatureX> featureX;',
    ref: '2026-06-17',
  },
  {
    id: 'response-header-after-commit',
    moduleCodes: ['C015', 'G059'],
    severity: 'high',
    keywords: ['response-header', 'after-commit', 'filter', 'beforecommit'],
    titleKey: 'showcase.pitfall.response-header-after-commit.title',
    symptomKey: 'showcase.pitfall.response-header-after-commit.symptom',
    causeKey: 'showcase.pitfall.response-header-after-commit.cause',
    fixKey: 'showcase.pitfall.response-header-after-commit.fix',
    ref: '#488~491',
  },
  {
    id: 'sse-logging-filter-commit',
    moduleCodes: ['C015', 'C008'],
    severity: 'high',
    keywords: ['sse', 'logging-filter', 'async', 'streaming', 'commit'],
    titleKey: 'showcase.pitfall.sse-logging-filter-commit.title',
    symptomKey: 'showcase.pitfall.sse-logging-filter-commit.symptom',
    causeKey: 'showcase.pitfall.sse-logging-filter-commit.cause',
    fixKey: 'showcase.pitfall.sse-logging-filter-commit.fix',
    ref: '#647~654',
  },
  {
    id: 'grpc-bind-external',
    moduleCodes: ['C062'],
    severity: 'medium',
    keywords: ['grpc', 'bind', 'external-exposure', 'loopback'],
    titleKey: 'showcase.pitfall.grpc-bind-external.title',
    symptomKey: 'showcase.pitfall.grpc-bind-external.symptom',
    causeKey: 'showcase.pitfall.grpc-bind-external.cause',
    fixKey: 'showcase.pitfall.grpc-bind-external.fix',
    ref: '2026-07-03',
  },
  {
    id: 'grpc-static-scheme',
    moduleCodes: ['C062'],
    severity: 'low',
    keywords: ['grpc', 'static-scheme', 'nameresolver', 'target'],
    titleKey: 'showcase.pitfall.grpc-static-scheme.title',
    symptomKey: 'showcase.pitfall.grpc-static-scheme.symptom',
    causeKey: 'showcase.pitfall.grpc-static-scheme.cause',
    fixKey: 'showcase.pitfall.grpc-static-scheme.fix',
    ref: '2026-07-03',
  },
  {
    id: 'timebomb-fixed-now-test',
    moduleCodes: ['C064', 'C047'],
    severity: 'medium',
    keywords: ['timebomb', 'clock', 'now', 'scheduled-test'],
    titleKey: 'showcase.pitfall.timebomb-fixed-now-test.title',
    symptomKey: 'showcase.pitfall.timebomb-fixed-now-test.symptom',
    causeKey: 'showcase.pitfall.timebomb-fixed-now-test.cause',
    fixKey: 'showcase.pitfall.timebomb-fixed-now-test.fix',
    ref: '#567~571',
  },
  {
    id: 'enablejparepositories-double',
    moduleCodes: ['C042', 'C068'],
    severity: 'high',
    keywords: ['jparepositories', 'double-registration', 'rootscan', 'startup'],
    titleKey: 'showcase.pitfall.enablejparepositories-double.title',
    symptomKey: 'showcase.pitfall.enablejparepositories-double.symptom',
    causeKey: 'showcase.pitfall.enablejparepositories-double.cause',
    fixKey: 'showcase.pitfall.enablejparepositories-double.fix',
    ref: '2026-06-14',
  },
  {
    id: 'mongo-repo-scan-isolation',
    moduleCodes: ['C054'],
    severity: 'medium',
    keywords: ['mongo', 'repository-scan', 'multistore', 'isolation'],
    titleKey: 'showcase.pitfall.mongo-repo-scan-isolation.title',
    symptomKey: 'showcase.pitfall.mongo-repo-scan-isolation.symptom',
    causeKey: 'showcase.pitfall.mongo-repo-scan-isolation.cause',
    fixKey: 'showcase.pitfall.mongo-repo-scan-isolation.fix',
    snippet: '@EnableMongoRepositories(basePackages = "com.skax.eraf.common.mongo")',
  },
  {
    id: 'permitall-antpath-npe',
    moduleCodes: ['C028', 'C029', 'C006'],
    severity: 'high',
    keywords: ['permitall', 'antpathmatcher', 'npe', 'requestcontext'],
    titleKey: 'showcase.pitfall.permitall-antpath-npe.title',
    symptomKey: 'showcase.pitfall.permitall-antpath-npe.symptom',
    causeKey: 'showcase.pitfall.permitall-antpath-npe.cause',
    fixKey: 'showcase.pitfall.permitall-antpath-npe.fix',
    ref: 'UAT 2026-05-12',
  },
  {
    id: 'yml-duplicate-key',
    moduleCodes: ['C085'],
    severity: 'medium',
    keywords: ['yml', 'duplicate-key', 'startup', 'config-merge'],
    titleKey: 'showcase.pitfall.yml-duplicate-key.title',
    symptomKey: 'showcase.pitfall.yml-duplicate-key.symptom',
    causeKey: 'showcase.pitfall.yml-duplicate-key.cause',
    fixKey: 'showcase.pitfall.yml-duplicate-key.fix',
    ref: 'UAT 2026-05-12',
  },
  {
    id: 'oom-zip-inmemory',
    moduleCodes: ['C009', 'C078'],
    severity: 'high',
    keywords: ['oom', 'zip', 'inmemory', 'acceptor', 'streaming'],
    titleKey: 'showcase.pitfall.oom-zip-inmemory.title',
    symptomKey: 'showcase.pitfall.oom-zip-inmemory.symptom',
    causeKey: 'showcase.pitfall.oom-zip-inmemory.cause',
    fixKey: 'showcase.pitfall.oom-zip-inmemory.fix',
    snippet: '-Xmx1536m -XX:+ExitOnOutOfMemoryError',
    ref: '#655',
  },
  {
    id: 'lombok-qualifier-drop',
    moduleCodes: ['C005'],
    severity: 'high',
    keywords: ['lombok', 'qualifier', 'requiredargsconstructor', 'prod-only'],
    titleKey: 'showcase.pitfall.lombok-qualifier-drop.title',
    symptomKey: 'showcase.pitfall.lombok-qualifier-drop.symptom',
    causeKey: 'showcase.pitfall.lombok-qualifier-drop.cause',
    fixKey: 'showcase.pitfall.lombok-qualifier-drop.fix',
    snippet: 'lombok.copyableAnnotations += org.springframework.beans.factory.annotation.Qualifier',
    ref: 'UAT 2026-06-02',
  },
  {
    id: 'filter-chain-granular-403',
    moduleCodes: ['C029', 'C030'],
    severity: 'medium',
    keywords: ['pathpolicy', 'securitychaincustomizer', '403', 'rule-order'],
    titleKey: 'showcase.pitfall.filter-chain-granular-403.title',
    symptomKey: 'showcase.pitfall.filter-chain-granular-403.symptom',
    causeKey: 'showcase.pitfall.filter-chain-granular-403.cause',
    fixKey: 'showcase.pitfall.filter-chain-granular-403.fix',
    ref: '2026-06-17',
  },
  {
    id: 'redis-repo-misclaim',
    moduleCodes: ['C050', 'C036'],
    severity: 'medium',
    keywords: ['redis', 'repository-scan', 'jpa-entity', 'misclaim'],
    titleKey: 'showcase.pitfall.redis-repo-misclaim.title',
    symptomKey: 'showcase.pitfall.redis-repo-misclaim.symptom',
    causeKey: 'showcase.pitfall.redis-repo-misclaim.cause',
    fixKey: 'showcase.pitfall.redis-repo-misclaim.fix',
    snippet: '@EnableRedisRepositories(basePackages = "com.skax.eraf.common.cache")',
    ref: '2026-06-17',
  },
  {
    id: 'otel-transport-port',
    moduleCodes: ['C072'],
    severity: 'low',
    keywords: ['otel', 'transport', 'grpc-4317', 'http-4318'],
    titleKey: 'showcase.pitfall.otel-transport-port.title',
    symptomKey: 'showcase.pitfall.otel-transport-port.symptom',
    causeKey: 'showcase.pitfall.otel-transport-port.cause',
    fixKey: 'showcase.pitfall.otel-transport-port.fix',
    ref: '#597~612',
  },
  {
    id: 'smoke-allow-failure-true',
    moduleCodes: ['C069', 'G060'],
    severity: 'medium',
    keywords: ['smoke', 'allow_failure', 'ci-gate', 'deploy'],
    titleKey: 'showcase.pitfall.smoke-allow-failure-true.title',
    symptomKey: 'showcase.pitfall.smoke-allow-failure-true.symptom',
    causeKey: 'showcase.pitfall.smoke-allow-failure-true.cause',
    fixKey: 'showcase.pitfall.smoke-allow-failure-true.fix',
    snippet: 'smoke:\n  allow_failure: false',
    ref: '#567~571',
  },
];
